import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Building2, Plus, Trash2, Users, Pencil, RotateCcw, Unlock } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const splitNames = (s) => (s || "").split(/[,\n]/).map((x) => x.trim()).filter(Boolean);

const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const label = "block text-sm font-medium mb-1.5";

export default function SuperAdmin() {
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", phone: "", invoice_prefix: "INV-", invoice_padding: 5, partners: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = async () => setCompanies((await api.get("/companies")).data);
  useEffect(() => { load(); }, []);

  const clampPad = (p) => Math.max(2, Math.min(parseInt(p) || 5, 8));
  const serialPreview = (prefix, next, pad) =>
    `${prefix || "INV-"}${String(Math.max(1, parseInt(next) || 1)).padStart(clampPad(pad), "0")}`;

  const save = async () => {
    try {
      if (!form.name) return toast.error("Company name required");
      await api.post("/companies", { ...form, invoice_padding: clampPad(form.invoice_padding), partners: splitNames(form.partners) });
      toast.success("Company created");
      setOpen(false);
      setForm({ name: "", address: "", phone: "", invoice_prefix: "INV-", invoice_padding: 5 });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (c) => {
    setEdit({
      id: c.id, name: c.name, phone: c.phone || "", address: c.address || "",
      invoice_prefix: c.invoice_prefix || "INV-",
      invoice_padding: c.invoice_padding || 5,
      invoice_next: c.invoice_next || 1,
      job_card_prefix: c.job_card_prefix || "",
      job_card_digits: c.job_card_digits || "",
      partners: (c.partners || []).join(", "),
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      if (!edit.name) return toast.error("Company name required");
      await api.put(`/companies/${edit.id}`, {
        name: edit.name, phone: edit.phone, address: edit.address,
        invoice_prefix: edit.invoice_prefix,
        invoice_padding: clampPad(edit.invoice_padding),
        invoice_next: Math.max(1, parseInt(edit.invoice_next) || 1),
        job_card_prefix: (edit.job_card_prefix || "").trim().toUpperCase(),
        job_card_digits: parseInt(edit.job_card_digits) || 0,
        partners: splitNames(edit.partners),
      });
      toast.success("Invoice settings updated");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const unlockOpening = async (c) => {
    if (!window.confirm(`Unlock the cash opening balance of ${c.name}? Its admin will be able to change it again.`)) return;
    try { await api.post(`/companies/${c.id}/opening-balance/unlock`); toast.success("Opening balance unlocked"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name} and all its data?`)) return;
    await api.delete(`/companies/${c.id}`);
    toast.success("Company deleted");
    load();
  };

  return (
    <Layout>
      <div className="p-8 max-w-5xl mx-auto">
        <PageHeader title="Companies" subtitle="Platform console — manage every company on Khata.">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="add-company-btn" className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors">
                <Plus size={16} /> New Company
              </button>
            </DialogTrigger>
            <DialogContent className="bg-card">
              <DialogHeader><DialogTitle className="font-display text-2xl">New Company</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><label className={label}>Company name</label>
                  <input data-testid="company-name" className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={label}>Phone</label>
                    <input data-testid="company-phone" className={field} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                  <div><label className={label}>Address</label>
                    <input data-testid="company-address" className={field} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={label}>Invoice prefix</label>
                    <input data-testid="company-prefix" className={field} value={form.invoice_prefix} onChange={(e) => setForm({ ...form, invoice_prefix: e.target.value })} /></div>
                  <div><label className={label}>Number padding</label>
                    <input data-testid="company-padding" type="number" min="2" max="8" className={field} value={form.invoice_padding} onChange={(e) => setForm({ ...form, invoice_padding: e.target.value })} /></div>
                </div>
                <div><label className={label}>Partners (capital accounts)</label>
                  <input data-testid="company-partners" className={field} value={form.partners} onChange={(e) => setForm({ ...form, partners: e.target.value })} placeholder="e.g. JAGGAN JI, MANI DEVI" />
                  <p className="text-xs text-muted-foreground mt-1">Separate names with commas. A capital account is created for each partner.</p></div>
              </div>
              <DialogFooter>
                <button data-testid="company-save" onClick={save} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Create</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </PageHeader>

        {companies.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
            <Building2 className="mx-auto mb-3 opacity-40" size={32} /> No companies yet. Create one to begin.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {companies.map((c) => (
              <div key={c.id} data-testid={`company-card-${c.id}`} className="rounded-xl border border-border bg-card p-5 animate-fade-up">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      <Building2 size={18} />
                    </div>
                    <div>
                      <div className="font-display text-lg font-semibold">{c.name}</div>
                      <div className="text-xs text-muted-foreground num">{c.phone || "—"} · {c.address || "—"}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.opening_locked && (
                      <button data-testid={`unlock-opening-${c.id}`} onClick={() => unlockOpening(c)} title="Opening balance is locked — click to unlock"
                        className="h-8 px-2.5 rounded-md border border-input text-xs flex items-center gap-1 text-accent hover:bg-muted transition-colors">
                        <Unlock size={13} /> Unlock opening
                      </button>
                    )}
                    <button data-testid={`edit-company-${c.id}`} onClick={() => openEdit(c)} className="text-muted-foreground hover:text-primary transition-colors" title="Edit invoice settings">
                      <Pencil size={16} />
                    </button>
                    <button data-testid={`del-company-${c.id}`} onClick={() => del(c)} className="text-muted-foreground hover:text-destructive transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                {(c.partners || []).length > 0 && (
                  <div data-testid={`company-partners-${c.id}`} className="mt-3 flex flex-wrap gap-1.5">
                    {c.partners.map((p) => <span key={p} className="text-[11px] px-2 py-0.5 rounded-full bg-muted">{p}</span>)}
                  </div>
                )}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border text-sm">
                  <span className="num text-muted-foreground" title="Next invoice number">next: {serialPreview(c.invoice_prefix, c.invoice_next, c.invoice_padding)}</span>
                  <Link to="/admin/users" className="flex items-center gap-1.5 text-primary hover:text-accent transition-colors font-medium">
                    <Users size={14} /> {c.user_count} users
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-card">
          <DialogHeader><DialogTitle className="font-display text-2xl">Company Settings</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><label className={label}>Company name</label>
                <input data-testid="edit-company-name" className={field} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={label}>Phone</label>
                  <input data-testid="edit-company-phone" className={field} value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} /></div>
                <div><label className={label}>Address</label>
                  <input data-testid="edit-company-address" className={field} value={edit.address} onChange={(e) => setEdit({ ...edit, address: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className={label}>Invoice prefix</label>
                  <input data-testid="edit-company-prefix" className={field} value={edit.invoice_prefix} onChange={(e) => setEdit({ ...edit, invoice_prefix: e.target.value })} /></div>
                <div><label className={label}>Padding (2-8)</label>
                  <input data-testid="edit-company-padding" type="number" min="2" max="8" className={field} value={edit.invoice_padding} onChange={(e) => setEdit({ ...edit, invoice_padding: e.target.value })} /></div>
                <div><label className={label}>Next serial #</label>
                  <input data-testid="edit-company-next" type="number" min="1" className={field} value={edit.invoice_next} onChange={(e) => setEdit({ ...edit, invoice_next: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><label className={label}>Job card / order no. prefix</label>
                  <input data-testid="edit-company-jc-prefix" className={field} value={edit.job_card_prefix} onChange={(e) => setEdit({ ...edit, job_card_prefix: e.target.value.toUpperCase() })} placeholder="e.g. JC/DMS/" /></div>
                <div><label className={label}>Digits</label>
                  <input data-testid="edit-company-jc-digits" type="number" min="0" max="10" className={field} value={edit.job_card_digits} onChange={(e) => setEdit({ ...edit, job_card_digits: e.target.value })} placeholder="any" /></div>
              </div>
              <p className="text-xs text-muted-foreground -mt-1">Job cards must be typed in this format, e.g. <span data-testid="edit-company-jc-example" className="num font-semibold">{(edit.job_card_prefix || "") + (parseInt(edit.job_card_digits) ? "1".padStart(parseInt(edit.job_card_digits), "0") : "123")}</span>. Leave both empty to allow any job card number.</p>
              <div><label className={label}>Partners (capital accounts)</label>
                <input data-testid="edit-company-partners" className={field} value={edit.partners} onChange={(e) => setEdit({ ...edit, partners: e.target.value })} placeholder="e.g. JAGGAN JI, MANI DEVI" />
                <p className="text-xs text-muted-foreground mt-1">Add names separated by commas. New names get a capital account; existing accounts are kept.</p></div>
              <div className="flex items-center justify-between rounded-md bg-muted/40 border border-border px-3 py-2.5">
                <div className="text-sm">
                  <div className="text-muted-foreground">Next invoice will be</div>
                  <div className="num font-semibold text-accent text-lg">{serialPreview(edit.invoice_prefix, edit.invoice_next, edit.invoice_padding)}</div>
                </div>
                <button data-testid="reset-serial-btn" onClick={() => setEdit({ ...edit, invoice_next: 1 })}
                  className="h-9 px-3 rounded-md border border-input bg-card text-sm flex items-center gap-1.5 hover:bg-muted transition-colors" title="Reset serial to 1 for a new financial year">
                  <RotateCcw size={14} /> Reset to 1
                </button>
              </div>
              <p className="text-xs text-muted-foreground">Set the next serial to 1 at the start of each financial year, or jump to any number. Existing invoices are unchanged.</p>
            </div>
          )}
          <DialogFooter>
            <button data-testid="edit-company-save" onClick={saveEdit} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
